import { isAxiosError } from 'axios';
import type {
  EdfFileError,
  ParsedEdfInfo,
} from '@lmt/signal-viewer-processor';
import type { TherapySettingsResult } from '@lmt/signal-viewer-ui';
import type { NotificationDispatcher } from '../components/notifications/NotificationProvider';
import {
  isFileUploadValidationError,
  MAX_UPLOAD_FILE_SIZE_LABEL,
  type FileUploadData,
} from '../utils/file-upload';
import { isSdCardError } from '../utils/sd-card/types';

const supportedFileDescription =
  'Try uploading a .psdat, .pdat, .pvdat, .pldat, .edf or .wmedf file.';

export function showFileUploadErrorNotification(
  notifications: NotificationDispatcher,
  error: unknown,
) {
  if (isFileUploadValidationError(error)) {
    if (error.code === 'unsupported-file-format') {
      notifications.showError({
        title: 'This file format is not supported',
        description: supportedFileDescription,
      });
      return;
    }

    if (error.code === 'file-too-large') {
      showFileTooLargeNotification(notifications);
      return;
    }

    if (error.code === 'corrupted-file') {
      notifications.showError({
        title: 'Corrupted file uploaded',
        description: getCorruptedFileDescription(error.fileNames),
      });
      return;
    }

    if (error.code === 'empty-selection') return;
  }

  if (isFileTooLargeApiError(error)) {
    showFileTooLargeNotification(notifications);
    return;
  }

  notifications.showError({
    title: 'File upload failed',
    description:
      error instanceof Error
        ? error.message
        : 'The selected file cannot be displayed.',
  });
}

export function showSdCardErrorNotification(
  notifications: NotificationDispatcher,
  error: unknown,
) {
  if (isSdCardError(error)) {
    notifications.showError({
      title: 'Could not read SD card',
      description: error.message,
    });
    return;
  }

  showFileUploadErrorNotification(notifications, error);
}

export function showParseErrorNotifications(
  notifications: NotificationDispatcher,
  errors: EdfFileError[],
) {
  for (const error of errors) {
    notifications.showError({
      title: `Corrupted file: ${error.fileName}`,
      description: "Data from this file can't be displayed.",
    });
  }
}

export function showTherapySettingsUnavailableNotification(
  notifications: NotificationDispatcher,
) {
  notifications.showWarning({
    title: "Therapy settings can't be shown",
    description:
      "Therapy settings can't be shown due to the firmware restrictions.",
    autoClose: false,
  });
}

export function showTherapySettingsErrorNotification(
  notifications: NotificationDispatcher,
  description: string,
) {
  notifications.showError({
    title: "Therapy settings can't be shown",
    description,
    autoClose: false,
  });
}

export function showEventsUnavailableNotification(
  notifications: NotificationDispatcher,
) {
  notifications.showWarning({
    title: "Events can't be shown",
    description: "Events couldn't be read from the uploaded file.",
    autoClose: false,
  });
}

export function showEventsErrorNotification(
  notifications: NotificationDispatcher,
  description: string,
) {
  notifications.showError({
    title: "Events can't be shown",
    description,
    autoClose: false,
  });
}

export function showTherapyArchiveErrorNotification(
  notifications: NotificationDispatcher,
  error: unknown,
) {
  notifications.showError({
    title: "Therapy data can't be loaded",
    description: getApiErrorDescription(error),
    autoClose: false,
  });
}

export function showUploadSuccessNotification(
  notifications: NotificationDispatcher,
  fileName: string,
) {
  notifications.showSuccess({
    title: 'File uploaded successfully!',
    description: fileName,
  });
}

export function showUnmatchedEventFilesNotificationIfNeeded(
  notifications: NotificationDispatcher,
  uploadedData: FileUploadData,
) {
  const names = uploadedData.unmatchedEventXmlNames ?? [];
  if (names.length === 0) return;

  notifications.showWarning({
    title: "Some event files couldn't be matched",
    description: `${names.join(', ')}: no matching signal file was found, so these events can't be displayed. Name files with matching stems (signal_X + event_X) to pair them.`,
  });
}

export function showOverlapNotificationIfNeeded(
  notifications: NotificationDispatcher,
  parsedInfo: ParsedEdfInfo[],
) {
  if (!hasOverlappingIntervals(parsedInfo)) return;

  notifications.showWarning({
    title: 'Overlapping data detected',
    description:
      'The uploaded file contains data overlapping with entries from another uploaded file.',
  });
}

export function isTherapySettingsUnavailable(
  therapySettings: TherapySettingsResult | null,
): boolean {
  return !therapySettings || therapySettings.sections.length === 0;
}

function hasOverlappingIntervals(parsedInfo: ParsedEdfInfo[]): boolean {
  const intervals = parsedInfo
    .map((info) => ({
      start: info.startMs,
      end: info.startMs + info.duration,
    }))
    .filter(
      (interval) =>
        Number.isFinite(interval.start) &&
        Number.isFinite(interval.end) &&
        interval.end > interval.start,
    )
    .sort((left, right) => left.start - right.start);

  for (let i = 1; i < intervals.length; i += 1) {
    if (intervals[i].start < intervals[i - 1].end) {
      return true;
    }
  }

  return false;
}

function getCorruptedFileDescription(fileNames: string[]): string {
  if (fileNames.length === 0) {
    return "Data from this file can't be displayed.";
  }

  return `${fileNames.join(', ')}: data from this file can't be displayed.`;
}

function showFileTooLargeNotification(notifications: NotificationDispatcher) {
  notifications.showWarning({
    title: 'File is too large',
    description: `Maximum file size is ${MAX_UPLOAD_FILE_SIZE_LABEL}.`,
  });
}

export function isFileTooLargeApiError(error: unknown): boolean {
  if (!isAxiosError(error)) return false;

  const status = error.response?.status;
  if (status === 413) return true;

  const responseText = getErrorResponseText(error.response?.data);
  return (
    status === 400 &&
    /too large|exceeds maximum|request body/i.test(responseText)
  );
}

function getErrorResponseText(data: unknown): string {
  if (typeof data === 'string') return data;
  if (!data || typeof data !== 'object') return '';

  const problem = data as { title?: unknown; detail?: unknown; errors?: unknown };
  return [problem.title, problem.detail, problem.errors]
    .filter(Boolean)
    .map((value) =>
      typeof value === 'string' ? value : JSON.stringify(value),
    )
    .join(' ');
}

function getApiErrorDescription(error: unknown): string {
  if (isAxiosError(error)) {
    const responseText = getErrorResponseText(error.response?.data);
    if (responseText) return responseText;
  }

  return error instanceof Error
    ? error.message
    : 'The backend could not read therapy data from the uploaded file.';
}
